/**
 * MiningProfitability — "¿qué ore minar ahora mismo para maximizar ISK/h?".
 *
 * Estilo card-grid (artifact 02): cada ore es una Card independiente con
 * hero distinto, ISK grande, mineral chips, magnitud bar. El top ore
 * recibe tratamiento "TOP" badge + border accent.
 *
 * Layout (de arriba a abajo):
 *   1. Header + LastUpdated + Refresh
 *   2. Slider + presets (mining rate)
 *   3. Tier filter chips
 *   4. Hero "Best ore" — card grande con nombre + ISK + tier pill
 *   5. Grid de OreCards (responsive: 1 col mobile, 2 col md, 3+ col xl)
 *   6. YieldBreakdown — aparece abajo cuando hay ore seleccionada (click)
 *   7. Footnote
 *
 * Persistencia (localStorage):
 *   - "mining.rate"      → mining rate (1-25)
 *   - "mining.tier"      → 'all' | 'high-sec' | 'low-sec' | 'null-sec'
 *   - "mining.selected"  → type_id de la ore seleccionada (o null)
 *
 * Caveats:
 *   - Solo ores communes; NO moon ores (R64) ni variantes raras.
 *   - Asume 1 unit ore = 0.1 m³ (EVE constant).
 *   - Tier badge = status color porque tier es dimensión de RIESGO, no
 *     identidad pura. Verde=seguro, amarillo=precaución, rojo=peligro.
 */

import { Fragment, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { Pill, StatTile, EmptyState, type PillTone } from '../components/ui'

type Tier = 'high-sec' | 'low-sec' | 'null-sec'
type TierFilter = Tier | 'all'

const TIER_LABEL: Record<Tier, string> = {
  'high-sec': 'High-sec',
  'low-sec': 'Low-sec',
  'null-sec': 'Null-sec',
}

const TIER_TONE: Record<Tier, PillTone> = {
  'high-sec': 'success',
  'low-sec': 'warn',
  'null-sec': 'danger',
}

const MIN_RATE = 1
const MAX_RATE = 25
const DEFAULT_RATE = 8

function readLS<T extends string>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  return (v as T) ?? fallback
}

function writeLS(key: string, value: string | number) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // localStorage lleno o deshabilitado — OK, solo no persistimos
  }
}

interface OreYield {
  mineral_type_id: number
  mineral_name: string
  yield_per_100: number
  jita_buy: number | null
  isk_per_hour: number
  pct_of_total: number
}

interface Ore {
  type_id: number
  name: string
  tier: Tier
  yields: OreYield[]
  total_isk_per_hour: number
}

export function MiningProfitability() {
  // Persisted state.
  const [rate, setRate] = useState<number>(() => {
    const stored = readLS<string>('mining.rate', String(DEFAULT_RATE))
    const parsed = parseInt(stored, 10)
    if (!Number.isFinite(parsed)) return DEFAULT_RATE
    return Math.min(MAX_RATE, Math.max(MIN_RATE, parsed))
  })
  const [tierFilter, setTierFilter] = useState<TierFilter>(() =>
    readLS<TierFilter>('mining.tier', 'all'),
  )
  const [selected, setSelected] = useState<number | null>(() => {
    const stored = readLS<string>('mining.selected', '')
    if (!stored) return null
    const parsed = parseInt(stored, 10)
    return Number.isFinite(parsed) ? parsed : null
  })
  const { key: forceKey, force, bump } = useForceRefetch()

  useEffect(() => writeLS('mining.rate', rate), [rate])
  useEffect(() => writeLS('mining.tier', tierFilter), [tierFilter])
  useEffect(
    () => writeLS('mining.selected', selected ?? ''),
    [selected],
  )

  const query = useQuery({
    queryKey: ['ore-profitability', rate, forceKey],
    queryFn: () => api.oreProfitability(rate),
    retry: false,
  })

  const ores = query.data?.ores ?? []
  const filtered = useMemo(
    () => (tierFilter === 'all' ? ores : ores.filter((o) => o.tier === tierFilter)),
    [ores, tierFilter],
  )

  // Counts por tier.
  const tierCounts = useMemo(() => {
    const counts: Record<Tier, number> = { 'high-sec': 0, 'low-sec': 0, 'null-sec': 0 }
    for (const o of ores) counts[o.tier]++
    return counts
  }, [ores])

  // Hero: best del filtro activo (o global si filter=all).
  const hero = filtered[0] ?? ores[0]
  const maxIskPerHour = Math.max(1, ...ores.map((o) => o.total_isk_per_hour))

  // Selected ore (para el breakdown de abajo). Default a la hero.
  const selectedOre =
    ores.find((o) => o.type_id === selected) ?? hero

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Mining profitability</h1>
          <p className="text-eve-muted text-sm">
            Ranking de ores communes por ISK/h usando precios Jita buy actuales.
            Ajustá la tasa de minería según tu hull (default 8 m³/s ≈
            Procurer/Retriever).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={query.dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={query.isFetching} />
        </div>
      </div>

      {/* Controls: slider + presets */}
      <div className="bg-eve-panel border border-eve-border rounded-md p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <label
            htmlFor="mining-rate"
            className="text-xs uppercase tracking-wider text-eve-muted"
          >
            Mining rate
          </label>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-lg font-semibold text-eve-text">
              {rate}
            </span>
            <span className="text-xs text-eve-muted">m³/s</span>
          </div>
        </div>
        <input
          id="mining-rate"
          type="range"
          min={MIN_RATE}
          max={MAX_RATE}
          step={1}
          value={rate}
          onChange={(e) => setRate(parseInt(e.target.value, 10))}
          className="w-full accent-eve-accent"
        />
        <div className="flex justify-between text-[10px] text-eve-muted-2 mt-1 font-mono">
          <span>{MIN_RATE}</span>
          <span>{DEFAULT_RATE} (Procurer)</span>
          <span>{MAX_RATE}</span>
        </div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {[
            { label: 'Venture', value: 1 },
            { label: 'Procurer', value: 8 },
            { label: 'Retriever', value: 12 },
            { label: 'Covetor', value: 18 },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setRate(preset.value)}
              className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                rate === preset.value
                  ? 'bg-eve-accent/10 border-eve-accent text-eve-accent'
                  : 'bg-eve-bg border-eve-border text-eve-muted hover:text-eve-text hover:border-eve-accent/40'
              }`}
            >
              {preset.label} · {preset.value}
            </button>
          ))}
        </div>
      </div>

      {/* Tier filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-eve-muted mr-1">
          Tier:
        </span>
        <button
          type="button"
          onClick={() => setTierFilter('all')}
          className="outline-none focus-visible:ring-1 focus-visible:ring-eve-accent/40 rounded-full"
        >
          <Pill tone={tierFilter === 'all' ? 'gold' : 'muted'} size="md">
            Todos · {ores.length}
          </Pill>
        </button>
        {(['high-sec', 'low-sec', 'null-sec'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTierFilter(t)}
            className="outline-none focus-visible:ring-1 focus-visible:ring-eve-accent/40 rounded-full"
          >
            <Pill
              tone={tierFilter === t ? TIER_TONE[t] : 'muted'}
              size="md"
              dot={tierFilter === t}
            >
              {TIER_LABEL[t]} · {tierCounts[t]}
            </Pill>
          </button>
        ))}
      </div>

      {/* Hero — best ore */}
      {query.data && hero && (
        <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-accent/30 rounded-md p-5 shadow-gold relative overflow-hidden">
          <div className="text-xs uppercase tracking-wider text-eve-muted">
            Best ore{tierFilter !== 'all' ? ` (${TIER_LABEL[tierFilter]})` : ' right now'}
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-3xl sm:text-4xl font-semibold text-eve-text">
              {hero.name}
            </span>
            <Pill tone={TIER_TONE[hero.tier]} size="md">
              {TIER_LABEL[hero.tier]}
            </Pill>
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-2xl sm:text-3xl text-eve-accent font-bold">
              {formatISKShort(hero.total_isk_per_hour)}
            </span>
            <span className="text-sm text-eve-muted">ISK/h</span>
            <span className="text-xs text-eve-muted-2">
              @ {rate} m³/s · Jita buy
            </span>
          </div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {hero.yields.map((y) => (
              <span
                key={y.mineral_type_id}
                className="text-[10px] font-mono bg-eve-bg/60 border border-eve-border/60 text-eve-text/90 px-1.5 py-0.5 rounded"
              >
                {y.mineral_name.slice(0, 3).toUpperCase()}{' '}
                <span className="text-eve-muted">{y.yield_per_100}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Error / loading */}
      {query.isError && (
        <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-4 text-eve-danger text-sm">
          {(query.error as Error).message}
        </div>
      )}
      {query.isLoading && <LoadingSkeleton variant="rows" rows={10} />}

      {/* Grid */}
      {query.data && (
        <>
          {filtered.length === 0 ? (
            <EmptyState
              title="Sin ores con este filtro"
              description="Probá cambiando el tier o el mining rate."
              variant="card"
            />
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-lg font-semibold text-eve-text">
                  Ranking
                </h2>
                <span className="text-[10px] text-eve-muted-2 italic">
                  click una card para ver el desglose por mineral
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filtered.map((ore) => {
                  const rank = ores.findIndex((o) => o.type_id === ore.type_id) + 1
                  const isBest = rank === 1
                  const isSelected = selectedOre?.type_id === ore.type_id
                  const barWidth =
                    ore.total_isk_per_hour > 0
                      ? Math.max(2, (ore.total_isk_per_hour / maxIskPerHour) * 100)
                      : 0
                  return (
                    <button
                      key={ore.type_id}
                      type="button"
                      onClick={() => setSelected(ore.type_id)}
                      className={`text-left rounded-md p-4 border transition-all relative outline-none focus-visible:ring-2 focus-visible:ring-eve-accent/50 ${
                        isSelected
                          ? 'border-eve-accent bg-eve-panel'
                          : isBest
                            ? 'border-eve-accent/40 bg-eve-panel hover:border-eve-accent/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30'
                            : 'border-eve-border bg-eve-panel hover:border-eve-accent/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30'
                      }`}
                    >
                      {isBest && (
                        <span className="absolute top-2 right-2 bg-eve-accent text-eve-bg text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                          ★ Top
                        </span>
                      )}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="font-display font-semibold text-lg text-eve-text truncate">
                            {ore.name}
                          </div>
                          <div className="text-[10px] text-eve-muted font-mono">
                            #{ore.type_id} · rank #{rank}
                          </div>
                        </div>
                        <Pill tone={TIER_TONE[ore.tier]} size="sm">
                          {TIER_LABEL[ore.tier]}
                        </Pill>
                      </div>

                      <div
                        className={`font-mono font-bold tabular-nums tracking-tight ${
                          isBest
                            ? 'text-2xl text-eve-accent'
                            : 'text-xl text-eve-text'
                        }`}
                      >
                        {formatISKShort(ore.total_isk_per_hour)}
                        <span className="text-xs text-eve-muted ml-1 font-sans">
                          ISK/h
                        </span>
                      </div>

                      <div className="mt-2 h-1 bg-eve-bg rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isBest ? 'bg-eve-accent' : 'bg-eve-accent/60'
                          }`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>

                      <div className="mt-3 flex gap-1 flex-wrap">
                        {ore.yields.map((y) => (
                          <span
                            key={y.mineral_type_id}
                            className="text-[10px] font-mono bg-eve-bg/60 border border-eve-border/60 text-eve-text/80 px-1.5 py-0.5 rounded"
                            title={`${y.mineral_name} · ${y.yield_per_100}/100 · ${formatISKShort(y.isk_per_hour)}/h (${y.pct_of_total.toFixed(0)}%)`}
                          >
                            {y.mineral_name.slice(0, 3).toUpperCase()}{' '}
                            <span className="text-eve-muted">{y.yield_per_100}</span>
                          </span>
                        ))}
                      </div>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Yield breakdown de la ore seleccionada */}
          {selectedOre && (
            <YieldBreakdown ore={selectedOre} />
          )}
        </>
      )}

      {/* Footnote */}
      {query.data && (
        <p className="text-xs text-eve-muted italic">
          Yields son base (sin refining skill) — el valor mostrado asume venta
          directa del mineral a Jita buy. Compresión/refining puede mejorar el
          ISK/h efectivo entre 5-15%. Sin impuestos de station ni taxes de
          corp aplicados.
        </p>
      )}
    </div>
  )
}

/**
 * YieldBreakdown — sección aparte que muestra el desglose completo de la
 * ore seleccionada. Tiene mini-barras por mineral (% del top mineral de
 * ESTE ore) y un StatTile de resumen.
 */
function YieldBreakdown({ ore }: { ore: Ore }) {
  const sorted = [...ore.yields].sort((a, b) => b.isk_per_hour - a.isk_per_hour)
  const maxYieldIsk = Math.max(1, ...sorted.map((y) => y.isk_per_hour))
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold text-eve-text">
          {ore.name} — yield breakdown
        </h2>
        <Pill tone={TIER_TONE[ore.tier]} size="sm">
          {TIER_LABEL[ore.tier]}
        </Pill>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          size="sm"
          tone="accent"
          label="Total ISK/h"
          value={formatISKShort(ore.total_isk_per_hour)}
        />
        <StatTile
          size="sm"
          label="Minerales"
          value={String(ore.yields.length)}
        />
        <StatTile
          size="sm"
          label="Top mineral"
          value={sorted[0]?.mineral_name ?? '—'}
          sub={
            sorted[0]
              ? `${formatISKShort(sorted[0].isk_per_hour)}/h · ${sorted[0].pct_of_total.toFixed(0)}%`
              : undefined
          }
        />
        <StatTile
          size="sm"
          label="Cycle time"
          value={`${sorted[0]?.yield_per_100 ?? 0}/100`}
          sub={`${ore.yields.length} ${ore.yields.length === 1 ? 'mineral' : 'minerales'}`}
        />
      </div>

      <div className="bg-eve-panel border border-eve-border rounded-md p-4">
        <div className="text-[10px] uppercase tracking-wider text-eve-muted mb-2">
          Desglose por mineral
        </div>
        <div className="space-y-1.5">
          {sorted.map((y) => {
            const barWidth =
              y.isk_per_hour > 0
                ? Math.max(2, (y.isk_per_hour / maxYieldIsk) * 100)
                : 0
            return (
              <div
                key={y.mineral_type_id}
                className="grid grid-cols-12 gap-2 items-baseline text-xs"
              >
                <div className="col-span-3 text-eve-text font-medium truncate">
                  {y.mineral_name}
                </div>
                <div className="col-span-2 font-mono text-eve-muted text-[11px]">
                  {y.yield_per_100}/100
                </div>
                <div className="col-span-2 font-mono text-eve-muted text-[11px]">
                  @ {y.jita_buy !== null ? formatISKShort(y.jita_buy) : '—'} ISK
                </div>
                <div className="col-span-2 font-mono text-right text-eve-text font-semibold">
                  {formatISKShort(y.isk_per_hour)}/h
                </div>
                <div className="col-span-3 flex items-center gap-2">
                  <div
                    className="flex-1 h-1 bg-eve-bg rounded overflow-hidden"
                    aria-hidden="true"
                  >
                    <div
                      className="h-full bg-eve-accent/70 rounded"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <span className="font-mono text-eve-accent text-[11px] tabular-nums w-8 text-right">
                    {y.pct_of_total.toFixed(0)}%
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
